import { configureStore } from '@reduxjs/toolkit';
import React, { useState } from 'react';
import { Provider } from 'react-redux';
import { RelayEnvironmentProvider } from 'react-relay';

import type { TasksQuery$variables } from '../src/graphql/__generated__/TasksQuery.graphql';
import { getRandomInt } from './functions';
import { getTableSlice } from './store/slices/tableSlice';
import TableMiddleware, { IOutTableDb } from './table/tableMiddleware';
import { ActionsType } from './types';

const storeStores: { [key: string]: any } = {};

const getStore = (name: string) => {
    if (storeStores[name]) {
        return storeStores[name];
    }

    storeStores[name] = configureStore({
        reducer: {
            table: getTableSlice(name),
        },
    });

    return storeStores[name];
};

const outTableDb = <T extends TasksQuery$variables, K, E>({
    nameQuery,
    paramFilter,
    paramSorter,
    environment,
    actions,
    ...RestProps
}: Omit<IOutTableDb<T, K, E>, 'prefixStore'> & { actions: Exclude<ActionsType, 'sorter' | 'filter'>[] }) => {
    const [prefixStore] = useState<string>(`${getRandomInt(1, 1000)}`);

    const extendedActions = [...(actions || [])] as ActionsType[];

    if (paramSorter) {
        extendedActions.push('sorter');
    }

    if (paramFilter) {
        extendedActions.push('filter');
    }

    const tableDbComponent = (
        <TableMiddleware<T, K, E>
            prefixStore={prefixStore}
            nameQuery={nameQuery}
            actions={extendedActions}
            paramFilter={paramFilter}
            paramSorter={paramSorter}
            {...RestProps}
        />
    );

    return (
        <Provider store={getStore(nameQuery + prefixStore)}>
            {environment ? (
                <RelayEnvironmentProvider environment={environment}>{tableDbComponent}</RelayEnvironmentProvider>
            ) : (
                tableDbComponent
            )}
        </Provider>
    );
};

export default outTableDb;
